/**
 * @file Purchase Album related helper functions.
 */
import get from 'lodash.get';

import { isPpd } from './album';
import { ALBUM_PRICE_MODE } from '../constants/albums';

const getPpaPrice = album => {
  return !album.is_demo
    ? parseInt(get(album, 'price_whole_album.value', 0), 10)
    : 0;
};

const getFolderSubscriptionPrice = album => {
  if (!get(album, 'subscription_album_folder.price')) {
    return undefined;
  }

  return !album.is_demo
    ? parseInt(get(album, 'subscription_album_folder.price.value'), 10)
    : 0;
};

const getAlbumPrice = album =>
  getFolderSubscriptionPrice(album) || getPpaPrice(album);

/**
 * Determines if an album is allowed to be joined without
 * requiring purchase.
 *
 * @param {Object} album - The album with all pricing info.
 * @returns {boolean} - Whether the album can be joined without purchase.
 */
const allowsJoinWithoutPurchase = album => {
  if (isPpd(album)) {
    return true;
  }

  return false;
};

/**
 * Given the identities for join & purchase, will prepare the checkout items object for joining
 * identities. These are to be joined by joinByAlbumJoinCode.
 *
 * @param {Array<Identity>} selectedIdentitiesForPurchase  Identities that will be purchased.
 * @param {Array<Identity>} selectedIdentitiesForJoin Identities that will be joined but *not* purchased.
 * @param {Album} album The album identities are purchasing/joining to.
 * @returns {Array}
 */
const getCheckoutItemsForJoin = (
  selectedIdentitiesForPurchase,
  selectedIdentitiesForJoin,
  album,
) => {
  if (!get(album, 'id')) {
    return [];
  }

  const identities = selectedIdentitiesForJoin
    .filter(
      identity =>
        // It is possible to have both selected (Join & Purchase). So clear the ones
        // that are joining for purchase. Shouldn't really affect the process at all,
        // but should make debugging easier in case we need to.
        !selectedIdentitiesForPurchase.find(({ id }) => id === identity.id),
    )
    .map(identity => identity.id);

  return identities;
};

/**
 * Given the identities for purchase, will prepare the checkout items object.
 *
 * @param {Array<Identity>} selectedIdentitiesForPurchase  Identities that will be purchased.
 * @param {Album} album The album identities are purchasing/joining to.
 * @returns {Array}
 */
const getCheckoutItemsForPurchase = (selectedIdentitiesForPurchase, album) => {
  if (!get(album, 'id')) {
    return [];
  }

  return selectedIdentitiesForPurchase.map(identity => ({
    identity_id: identity.id,
    ...(get(album, 'subscription_album_folder.id', undefined)
      ? {
          album_folder_id: album.subscription_album_folder.id,
          join_albums: [album.id],
        }
      : {
          albums: [album.id],
        }),
  }));
};

/**
 * Given an AlbumMembership with an Album (with its root AlbumFolder required pricing information)
 * and the totals of both matched photos and entitled photos, it determines
 * at which ID the user should be redirected to in the matches view (ie Album id or the AlbumFolder id).
 *
 * @param {Object} albumMembership The AlbumMembership.
 * @returns {string} The redirect ID (AlbumFolder ID or Album ID).
 */
const getAlbumMembershipBranchLinkRedirectId = albumMembership => {
  const album = albumMembership?.album;

  if (!album) {
    return '';
  }

  const root = album.root_album_folder;

  if (!root || root?.individual_albums) {
    return album.id;
  }

  const isPpa =
    root?.album_price_mode === ALBUM_PRICE_MODE.PPA_AND_PPD ||
    root?.album_price_mode === ALBUM_PRICE_MODE.PPA_ONLY;

  // Redirect to the AlbumFolder on all times where we don't have PPA
  return !isPpa ? root.id : album.id;
};

export {
  getCheckoutItemsForJoin,
  getCheckoutItemsForPurchase,
  getPpaPrice,
  getFolderSubscriptionPrice,
  getAlbumPrice,
  allowsJoinWithoutPurchase,
  getAlbumMembershipBranchLinkRedirectId,
};
